import { VoucherCampaignType } from '@src/enums'
import {
    CancelCounterStart,
    KycFieldRestrictionMode,
    KycProvider,
    PlatformComplianceProfile,
    PlatformPolicyType,
    PlatformWebhookAction,
} from '@src/enums/Platform'
import { ImageInterface } from '@src/types'
import { PaymentAccount } from './PaymentAccount'

export class Platform {
    platform_uid: string | null = null
    parent_platform_name: string | null = null
    platform_name: string | null = null
    platform_url: string | null = null
    platform_logo: ImageInterface | null = null
    terms_url: string | null = null
    privacy_url: string | null = null
    admin_email: string | null = null
    created_by: string | null = null
    created_on: string | null = null
    updated_at: string | null = null
    url_pattern: string | null = null
    mail_template: 'standard' | null = null
    mail_template_color: string | null = null
    mail_template_action_button_color: string | null = null
    mail_template_action_button_text_color: string | null = null
    mail_active_acceptance_notice = false
    mail_active_bank_account_information_reminder = false
    mail_active_basket_reminder = false
    mail_active_consumer_information = false
    mail_active_external_kyc_status_pending = false
    mail_active_external_kyc_status_success = false
    mail_active_external_kyc_process_aborted = false
    mail_active_kyc_reminder = false
    mail_active_kyc_status = false
    mail_active_kyc_expire_reminder = false
    mail_active_no_iban_reminder = false
    mail_active_payment_confirmation = false
    mail_active_payment_details = false
    mail_active_payment_reminder = false
    mail_active_pre_consumer_information = false
    mail_active_signature_reminder = false
    mail_active_signature_successful = false
    mail_active_signature_regenerated = false
    mail_active_welcome = false
    mail_active_interest_payment_announcement = false
    mail_active_interest_payment_payout_received = false
    mail_active_signatures_regenerated = false
    mail_active_voucher_received = false
    mail_active_feedback_review = false
    mail_active_wallet_balance = false
    mail_active_voucher_balance = false
    mail_active_membership_document_reminder = false
    mail_encryption: string | null = null
    mail_from_name: string | null = null
    mail_from_address: string | null = null
    mail_host: string | null = null
    mail_port: number | null = null
    mail_username: string | null = null
    is_platform = false
    is_realm = false
    mail_password: string | null | undefined = null
    payment_account_id: string | null = null
    webhooks_activated = false
    clearing_systems: PlatformClearingSystem[] = []
    attachments: PlatformAttachment[] = []
    documents: PlatformDocument[] = []
    disclaimers: PlatformDisclaimer[] = []
    email_templates: PlatformEmailTemplate[] = []
    policies: PlatformPolicy[] = []
    webhooks: PlatformWebhook[] = []
    funding_total_status: number[] = []
    effective_funding_total_status: number[] = []
    voucher_wallet: string | null = null
    voucherify_campaigns: VoucherCampaign[] = []
    has_credential_simply_tokenized = false
    has_credential_sproof = false
    has_credential_voucherify = false
    payment_qr_code_type: string | null = null
    esac_kyc_active = false
    esac_form_natural_person = new EsackycAttachment()
    esac_form_legal_entity = new EsackycAttachment()
    esac_form_legal_ubo = new EsackycAttachment()
    acceptance_document_active = false
    acceptance_documents: PlatformAcceptanceDocument[] = []
    compliance_profile: PlatformComplianceProfile | null = null
    self_disclosure_forms: SelfDisclosureForm[] = []
    membership_management_active = false
    membership_type_manual = false
    membership_type_sproof = false
    qualified_signature = false
    use_rule = false
    membership_number_active = false
    gift_membership = false
    gift_membership_faq: string | null = null
    gift_membership_faq_url: string | null = null
    membership_number: string | null = null
    membership_number_prefix_natural: string | null = null
    membership_number_prefix_legal: string | null = null
    membership_documents: PlatformMembershipDocument[] = []
    membership_document_description: string | null = null
    requested_membership_documents_email: string | null = null
    certificate_document = new PlatformCertificateDocument()
    certificate_active = false
    tax_documents: PlatformTaxDocument[] = []
    external_kyc_active = false
    kyc_provider: KycProvider | null = null
    signteq_api_host: string | null = null
    signteq_redirect_host: string | null = null
    signteq_api_key: string | null = null
    signteq_customer: string | null = null
    signteq_signature_api_key: string | null = null
    signteq_signature_organization_id: string | null = null
    external_kyc_api_host: string | null = null
    external_kyc_redirect_host: string | null = null
    external_kyc_api_key: string | null = null
    external_kyc_customer: string | null = null
    kyc_field_restriction_mode: KycFieldRestrictionMode | null = null
    investor_profile_field_settings: InvestorProfileFieldSettings | null = null
    zoho_sync_enabled = false
    zoho_sync_inherited = false
    zoho_environment: string | null = null
    zoho_webhook_url: string | null = null
    zoho_creator_owner: string | null = null
    zoho_creator_app_name: string | null = null
    zoho_creator_server: string | null = null
    zoho_client_id: string | null = null
    zoho_client_secret: string | null = null
    zoho_redirect_uri: string | null = null
    zoho_refresh_token: string | null = null
    zoho_api_domain: string | null = null
    workdrive_client_id: string | null = null
    workdrive_client_secret: string | null = null
    workdrive_refresh_token: string | null = null
    workdrive_api_domain: string | null = null
    workdrive_team_folder_id: string | null = null
    order_transfer_enabled = false
    voucherify_enabled = false
    investment_desk_settings = new PlatformInvestmentDeskSettings()
    agio_disagio_feature_active = false
    payment_account: PaymentAccount | null = null
    require_kyc_id_for_investments = true
    require_bank_account_for_investments = false
    require_kyc_address_for_investments = false
    require_kyc_tax_for_investments = false
    signteq_kyc_qes = true
    redirect_to_another_platform = false
    redirection_url: string | null = null
    redirect_on_login = false
    redirect_on_register = false
    corporate_kyc_support_recipients_registration: string[] = []
    corporate_kyc_support_recipients_document_submit: string[] = []
    kyc_reset_threshold: number | null = null
    cancel_order_button_enabled = false
    cancel_counter_start: CancelCounterStart | null = null
    cancel_period_days: number | null = null
    cancel_contact_email: string | null = null
    cancel_notification_recipients: string[] = []
    acceptance_after_cancel_period = false
    payout_through_acceptance = false
    cancel_auto_refund_enabled = false
    primary_color: string | null = null
    badge_colors: Record<string, string> | null = null
    sm_marketplace = false
    sm_trading_enabled = false
    sm_dashboard_marketplace_button_enabled = false
    sm_dashboard_back_to_platform_button_enabled = false
    sm_marketplace_listings_visible = false
    sm_payment_confirmation_deadline: number | null = null
    sm_payment_fulfillment_deadline: number | null = null
    sm_signing_deadline: number | null = null
    sm_escalation_emails: string[] = []
    sm_required_base_data_fields: string[] = []
    sm_frontend_url: string | null = null
    sm_required_base_data_field_options: string[] = []
    show_wallet_menu_item = true
    show_news_menu_item = true
    user_feedback_provider: string | null = null
    user_feedback_pushfeedback_project: string | null = null
    user_feedback_pushfeedback_api_key: string | null = null
    user_feedback_usersnap_project_code: string | null = null
}

export class InvestorProfileFieldSetting {
    visible = true
    required = false
    editable_after_kyc = true
}

export class InvestorProfileFieldSettings {
    personal_data: Record<string, InvestorProfileFieldSetting> = {}
}

export class PlatformInvestmentDeskSettings {
    use_new_theme = false
    single_campaign_style: string | null = null
    lock_details_tab = false
    lock_documents_tab = false
    lock_investors_room_tab = false
    show_tos_for_unauthenticated_users = false
    show_minimum_investment_instead_of_minimum_funding = false
    hide_percentage_on_knob = false
    hide_funding_indicator_on_knob = false
    hide_funding_max_on_knob = false
    show_dropdown_on_hover = false
    hide_company_account = false
    hide_create_company_account = false
    hide_latest_investments = false
    wallet_currency: string | null = null
    show_language_select_menu_in_header = false
    onboarding_flow = false
    use_v2_frontend = false
    use_v2_frontend_investment_process = false
    use_v2_frontend_portfolio_dashboard = false
    use_v2_frontend_portfolio_investments = false
    use_voucherify_coupon_code = false
    emission_use_asset_title_in_preview_calculation = false
    emission_hide_investment_amount_row = false
    show_contract_interest_rate_investment_process = false
    restrict_campaign_access_to_kyc_identity_verified_users = false
    redirect_url_after_login: string | null = null
    login_only_via_external_identity_provider: string | null = null
    hide_cent_values = false
    use_swiss_currency_format = false
    queue_it = false
    queue_it_customer_id: string | null = 'conda'
    right_of_withdrawal = false
    consequences_of_revocation = false
    new_login_modal_translations = false
    show_depot_fields_on_profile_section = false
    confetti_on_investment_completion = true
    confetti_in_onboarding = true
}

export class PlatformAcceptanceDocument {
    id?: string | null = null
    uuid?: string | null = null
    name?: string | null = null
    file_name?: string | null = null
    thumb_image_url?: string | null = null
    url?: string | null = null
    upload_key?: string | null = null
    delete?: boolean | null = null
    language: string | null = null
}

export class PlatformMembershipDocument {
    id?: string | null = null
    uuid?: string | null = null
    name?: string | null = null
    file_name?: string | null = null
    thumb_image_url?: string | null = null
    url?: string | null = null
    upload_key?: string | null = null
    delete?: boolean | null = null
    language: string | null = null
    description: string | null = null
}

export class PlatformCertificateDocument {
    id?: string | null = null
    uuid?: string | null = null
    name?: string | null = null
    file_name?: string | null = null
    thumb_image_url?: string | null = null
    url?: string | null = null
    upload_key?: string | null = null
    delete?: boolean | null = null
    lang: string | null = null
}

export class PlatformTaxDocument {
    id?: string | null = null
    uuid: string | null = null
    name?: string | null = null
    file_name?: string | null = null
    thumb_image_url?: string | null = null
    url?: string | null = null
    upload_key?: string | null = null
    delete?: boolean | null = null
    language: string | null = null
}

export class SelfDisclosureForm {
    id?: string | null = null
    uuid?: string | null = null
    name?: string | null = null
    thumb_image_url?: string | null = null
    url?: string | null = null
    upload_key?: string | null = null
    delete?: boolean | null = null
    language: string | null = null
}

export interface PlatformClearingSystem {
    id?: string | null
    clearing_system: string
}

export class PlatformAttachment {
    id?: string | null
    name: string | null = null
    type: string | null = null
    url: string | null = null
    upload_key: string | null = null
    language: string | null = null
}

export class PlatformDocument {
    id?: string | null = null
    name: string | null = null
    url: string | null = null
    file_name?: string | null = null
    mime_type?: string | null = null
    size?: number | null = null
    upload_key: string | null = null
    language: string | null = null
}

export class PlatformDisclaimer {
    id?: string
    platform_uid: string | null = null
    risk_title: string | null = null
    risk_content: string | null = null
    language: string | null = null
    countries_applied: string[] | null = null
    countries_not_applied: string[] | null = null
}

export class PlatformEmailTemplate {
    id?: string
    platform_uid?: string
    language: string | null = null
    number_locale: string | null = null
    subject: string | null = null
    template_name: string | null = null
    template_content: string | null = null
    inherited?: boolean
}

export interface PlatformEmailTemplateOption {
    name: string
    value: string
    for_platform: boolean
    for_realm: boolean
}

export class PlatformPolicy {
    id?: string
    country: string | null = null
    language: string | null = null
    platform_uid?: string
    policy_name: PlatformPolicyType | null = null
    policy_content: string | null = null
}

export class PlatformWebhook {
    id?: string
    url: string | null = null
    action: PlatformWebhookAction | null = null
    secret: string | null = null
}

export class VoucherCampaign {
    id: string | null = null
    voucherify_campaign_name: string | null = null
    type: VoucherCampaignType = VoucherCampaignType.REFERRAL_PROGRAM
    default = false
    disabled = false
    created_at: Date | null = null
}

export class EsackycAttachment {
    name?: string | null = null
    thumb_image_url?: string | null = null
    upload_key?: string | null
    delete?: boolean | null = null
}
